import { Link } from 'react-router';
import PropertyCard from '../../components/PropertyCard.jsx';
import { EmptyState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { WelcomeBanner } from '../../components/WelcomeBanner.jsx';
import { api } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { formatXaf, rentPeriodNoun, timeAgo } from '../../lib/format.js';

/**
 * What the landlord has put out to everybody on the compound.
 *
 * Top of the page, above the tenant's own figures, because a water cut on
 * Saturday matters more to somebody signing in than their rent balance. It
 * stays there until they dismiss it - a notice nobody acknowledged is one the
 * landlord can see has not landed.
 */
const Notices = ({ items, onDismiss }) => {
  if (!items.length) return null;

  return (
    <div className="notice-stack">
      {items.map((item) => (
        <article key={item.id} className={`notice ${item.urgent ? 'notice-warn' : 'notice-info'} tenant-notice`}>
          <div className="tenant-notice-text">
            <strong>{item.title}</strong>
            <p>{item.body}</p>
            <span className="muted">
              From {item.from}
              {item.property ? ` - about ${item.property.title}` : ''} - {timeAgo(item.createdAt)}
            </span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onDismiss(item.id)}>
            Got it
          </button>
        </article>
      ))}
    </div>
  );
};

const TenantDashboard = () => {
  useTitle('My dashboard');
  const { user, counts } = useAuth();

  const notices = useFetch('/me/announcements');
  const applications = useFetch('/applications/mine');
  const favorites = useFetch('/favorites');
  const rent = useFetch('/tenant/rent');
  const leases = useFetch('/tenancies?status=any');

  const items = applications.data?.items || [];
  const saved = favorites.data?.items || [];
  const open = items.filter((item) => ['submitted', 'under_review'].includes(item.status));
  const approved = items.filter((item) => item.status === 'approved');
  const liveRent = (rent.data?.items || []).find((tenancy) => tenancy.status === 'active');
  const toSign = (leases.data?.items || []).filter(
    (lease) => lease.status === 'awaiting_signatures' && !lease.signatures?.tenant
  );

  return (
    <div className="container section">
      <WelcomeBanner
        eyebrow="Welcome back,"
        title={user.name.split(' ')[0]}
        text="Everything you have applied for and saved, and every bill you have to pay, in one place."
      >
        <Link to="/my/utilities" className="btn btn-primary">
          Pay a bill
        </Link>
        <Link to="/search" className="btn btn-outline">
          Find a house
        </Link>
      </WelcomeBanner>

      <Notices
        items={notices.data?.items || []}
        onDismiss={async (id) => {
          // Dismissed on the server so it stays dismissed on their phone too.
          await api.post(`/me/announcements/${id}/read`);
          notices.reload();
        }}
      />

      {toSign.length > 0 ? (
        <div className="notice notice-warn">
          <strong>Your tenancy agreement is waiting for your signature.</strong> Read it through and sign it online.{' '}
          <Link to="/tenant/lease">Open the lease</Link>.
        </div>
      ) : null}

      {liveRent ? (
        <>
          <div className="stat-row">
            <div className={`stat-tile ${liveRent.ledger.totals.overdue > 0 ? 'tone-warn' : ''}`}>
              <span className="stat-value">
                {formatXaf(liveRent.ledger.totals.dueNow ?? Math.max(liveRent.ledger.totals.balance, 0))}
              </span>
              <span className="stat-label">
                {(liveRent.ledger.totals.dueNow ?? 0) > 0 ? 'Rent due now' : 'Rent account up to date'}
              </span>
              <span className="stat-note">
                {liveRent.ledger.totals.nextDue
                  ? `Next: ${liveRent.ledger.totals.nextDue.label}`
                  : 'Nothing outstanding'}
              </span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">{formatXaf(liveRent.rent)}</span>
              <span className="stat-label">Your rent each {rentPeriodNoun(liveRent.billingPeriod)}</span>
              <span className="stat-note">
                {liveRent.unit?.label ? `${liveRent.unit.label}, ` : ''}
                {liveRent.property?.title}
              </span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">
                {liveRent.ledger.payments.filter((payment) => !payment.voided).length}
              </span>
              <span className="stat-label">Receipts on file</span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">{counts.messages}</span>
              <span className="stat-label">Unread messages</span>
            </div>
          </div>

          <div className="row-actions dashboard-links">
            <Link to="/tenant/rent" className="link-strong">
              Pay rent and see receipts
            </Link>
            <Link to="/tenant/lease" className="link-strong">
              My lease
            </Link>
          </div>
        </>
      ) : null}

      <div className="stat-row">
        <div className="stat-tile">
          <span className="stat-value">{open.length}</span>
          <span className="stat-label">Applications waiting on a decision</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{approved.length}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{saved.length}</span>
          <span className="stat-label">Houses saved</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{counts.messages}</span>
          <span className="stat-label">Unread messages</span>
        </div>
      </div>

      <section className="section-block">
        <div className="section-head">
          <h2>Your applications</h2>
          <Link to="/tenant/applications" className="link-strong">
            See all
          </Link>
        </div>

        {applications.loading ? <Spinner /> : null}

        {!applications.loading && items.length === 0 ? (
          <EmptyState
            title="You have not applied for anything yet"
            message="When you find a house you like, use Apply now on the listing. Your details are saved so the second application is quicker."
            action={
              <Link to="/search" className="btn btn-primary">
                Browse listings
              </Link>
            }
          />
        ) : null}

        <ul className="application-strip">
          {items.slice(0, 4).map((application) => (
            <li key={application.id}>
              <Link to={`/properties/${application.propertyId}`} className="application-strip-media">
                {application.property?.coverImage ? (
                  <img src={application.property.coverImage} alt="" />
                ) : (
                  <span className="property-card-noimage">No photo</span>
                )}
              </Link>
              <div>
                <StatusBadge status={application.status} />
                <strong>{application.property?.title || 'Listing removed'}</strong>
                <span className="muted">
                  {application.property ? `${formatXaf(application.property.price)} / month - ` : ''}
                  applied {timeAgo(application.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="section-block">
        <div className="section-head">
          <h2>Saved houses</h2>
          <Link to="/tenant/saved" className="link-strong">
            See all
          </Link>
        </div>

        {favorites.loading ? <Spinner /> : null}

        {!favorites.loading && saved.length === 0 ? (
          <EmptyState
            title="Nothing saved yet"
            message="Tap the heart on any listing to keep it here while you make up your mind."
          />
        ) : (
          <div className="card-grid">
            {saved.slice(0, 3).map((property) => (
              <PropertyCard key={property.id} property={property} compact />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default TenantDashboard;
